import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { FormSection } from '@/components';
import { GlassCard } from '@/components/shared/redesign';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { roleNamesAndDescriptions } from '@/types/admin/userManagement';

interface UserPreviewSectionProps {
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    roleName: string;
    organizations?: string[];
    properties?: string[];
  };
}

export const UserPreviewSection: React.FC<UserPreviewSectionProps> = ({
  formData,
}) => {
  const styles = useStyles();
  // Find the role details
  const roleDetails = Object.values(roleNamesAndDescriptions).find(
    (role) => role.value === formData.roleName,
  );

  return (
    <>
      {/* Step Indicator */}
      <View style={styles.stepIndicator}>
        <Text style={styles.stepText}>Step 3 of 3: Preview</Text>
      </View>

      <FormSection
        title="Review User Details"
        subtitle="Please review the information before creating the user"
      >
        <View style={styles.container}>
          {/* Role Information */}
          <GlassCard padding={16}>
            <Text style={styles.roleTitle}>{roleDetails?.name || formData.roleName}</Text>
            <Text style={styles.roleDescription}>
              {roleDetails?.description || 'Custom role permissions'}
            </Text>
          </GlassCard>

          {/* User Information */}
          <View style={styles.infoSection}>
            <InfoField label="First Name" value={formData.firstName} />
            <InfoField label="Last Name" value={formData.lastName} />
            <InfoField label="Email Address" value={formData.email} />
          </View>

          {/* Organizations (if applicable) */}
          {formData.organizations && formData.organizations.length > 0 && (
            <View style={styles.infoSection}>
              <Text style={styles.sectionTitle}>Related Organizations</Text>
              {formData.organizations.map((org, index) => (
                <Text key={index} style={styles.listItem}>• {org}</Text>
              ))}
            </View>
          )}

          {/* Properties (if applicable) */}
          {formData.properties && formData.properties.length > 0 && (
            <View style={styles.infoSection}>
              <Text style={styles.sectionTitle}>Related Properties</Text>
              {formData.properties.map((prop, index) => (
                <Text key={index} style={styles.listItem}>• {prop}</Text>
              ))}
            </View>
          )}
        </View>
      </FormSection>
    </>
  );
};

interface InfoFieldProps {
  label: string;
  value: string;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => {
  const styles = useStyles();
  return (
    <View style={styles.infoField}>
      <Text style={styles.infoLabel}>{label.toUpperCase()}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      stepIndicator: {
        backgroundColor: 'transparent',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
        marginBottom: 16,
      },
      stepText: {
        fontSize: 14,
        fontWeight: '500',
        color: components.admin.dataCell.textMuted,
        textAlign: 'center',
      },
      container: {
        gap: 24,
      },
      roleTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 8,
      },
      roleDescription: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
        lineHeight: 20,
      },
      infoSection: {
        gap: 12,
      },
      sectionTitle: {
        fontSize: 14,
        fontWeight: '600',
        color: colors.text.primary,
        marginBottom: 4,
      },
      infoField: {
        gap: 4,
      },
      infoLabel: {
        fontSize: 12,
        fontWeight: '500',
        color: components.admin.dataCell.textMuted,
        letterSpacing: 0.5,
      },
      infoValue: {
        fontSize: 16,
        color: colors.text.primary,
      },
      listItem: {
        fontSize: 14,
        color: colors.text.primary,
        marginLeft: 8,
      },
    }),
  );
